// Shared Finance-style design tokens for the Building Creation feature.
// Mirrors the look used in src/neetjee_guru/components/FeeStructure (indigo/violet
// cards, rounded inputs, gradient buttons, pill badges) so the whole module reads
// as one consistent UI.

const S = {
    page: { padding: "4px 0" },

    card: {
        background: "#fff",
        borderRadius: 20,
        boxShadow: "0 2px 16px rgba(0,0,0,0.07)",
        border: "none",
        marginBottom: 24,
        overflow: "hidden",
    },

    cardHeader: {
        background: "#ffffff",
        padding: "20px 24px",
        display: "flex",
        alignItems: "center",
        gap: 14,
        borderBottom: "1px solid #f1f5f9",
        flexWrap: "wrap",
    },

    iconWrapperCreate: {
        width: 38,
        height: 38,
        borderRadius: 10,
        background: "#e0e7ff",
        border: "1px solid #c7d2fe",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#4f46e5",
        fontSize: 16,
        flexShrink: 0,
    },

    iconWrapperList: {
        width: 38,
        height: 38,
        borderRadius: 10,
        background: "#f5f3ff",
        border: "1px solid #ddd6fe",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#7c3aed",
        fontSize: 16,
        flexShrink: 0,
    },

    cardTitleText: { display: "flex", flexDirection: "column", textAlign: "left" },
    cardTitle: { color: "#0f172a", fontWeight: 700, fontSize: 16, margin: 0, letterSpacing: "-0.01em" },
    cardSubtitle: { color: "#64748b", fontSize: 12, fontWeight: 500, marginTop: 2 },

    cardBody: { padding: "22px 24px" },

    label: {
        fontSize: 12,
        fontWeight: 600,
        color: "#6b7280",
        marginBottom: 6,
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        display: "block",
    },

    input: {
        borderRadius: 10,
        border: "1.5px solid #e5e7eb",
        padding: "9px 14px",
        fontSize: 14,
        color: "#1f2937",
        background: "#f9fafb",
        width: "100%",
        height: 42,
    },

    inputError: {
        borderRadius: 10,
        border: "1.5px solid #ef4444",
        padding: "9px 14px",
        fontSize: 14,
        color: "#1f2937",
        background: "#f9fafb",
        width: "100%",
        height: 42,
    },

    // Primary brand action (indigo) — used for "New Building" / "New Allocation".
    addBtn: {
        background: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
        border: "none",
        borderRadius: 10,
        padding: "9px 20px",
        fontWeight: 700,
        fontSize: 14,
        color: "#fff",
        boxShadow: "0 4px 12px rgba(99,102,241,0.3)",
        cursor: "pointer",
        marginLeft: "auto",
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
    },

    // Submit / confirm action (green).
    submitBtn: {
        background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
        border: "none",
        borderRadius: 10,
        padding: "9px 24px",
        fontWeight: 700,
        fontSize: 14,
        color: "#fff",
        boxShadow: "0 4px 12px rgba(16,185,129,0.3)",
        cursor: "pointer",
        height: 42,
        width: "100%",
    },

    errText: { color: "#ef4444", fontSize: 12, marginTop: 4 },

    countChip: {
        background: "#f5f3ff",
        color: "#7c3aed",
        borderRadius: 20,
        padding: "4px 12px",
        fontWeight: 600,
        fontSize: 12,
        border: "1px solid #ddd6fe",
        marginLeft: "auto",
    },

    // Small stat pill used inside accordion headers (Floors / Rooms / Capacity ...).
    statChip: {
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        background: "#eef2ff",
        color: "#4338ca",
        borderRadius: 8,
        padding: "3px 10px",
        fontWeight: 600,
        fontSize: 12,
        border: "1px solid #e0e7ff",
    },

    noData: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "48px 0",
        color: "#94a3b8",
    },

    // Always-visible circular close button for modals (Bootstrap 4's default is faint).
    modalClose: {
        marginLeft: "auto",
        width: 34,
        height: 34,
        borderRadius: "50%",
        border: "none",
        background: "#f1f5f9",
        color: "#475569",
        fontSize: 22,
        lineHeight: 1,
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
    },

    // White inner panel used inside modal bodies.
    modalCard: {
        background: "#fff",
        borderRadius: 16,
        padding: "20px 22px",
        boxShadow: "0 2px 12px rgba(0,0,0,0.05)",
        border: "1px solid #eef2f7",
        position: "relative",
    },

    // Spinner overlay shown over the modal form while existing building
    // details load (edit mode) so the user sees feedback instead of an empty form.
    loadingOverlay: {
        position: "absolute",
        inset: 0,
        background: "rgba(255,255,255,0.78)",
        borderRadius: 16,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10,
    },

    // Column header label inside the floor/room config grid.
    colHead: {
        fontSize: 12,
        fontWeight: 700,
        color: "#475569",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        textAlign: "center",
    },

    // Section heading (e.g. "Configure Rooms").
    sectionHead: {
        fontSize: 13,
        fontWeight: 700,
        color: "#4f46e5",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        display: "flex",
        alignItems: "center",
    },

    // ── Master–detail layout (building list on the left, drill-down on the right) ──
    // Used to keep large building/floor/room datasets readable: only the selected
    // building's floors & rooms are rendered at a time, never the whole tree at once.
    mdWrap: {
        display: "flex",
        gap: 18,
        marginTop: 18,
        alignItems: "flex-start",
        flexWrap: "wrap",
    },

    // Left pane: searchable, scrollable list of buildings.
    listPane: {
        flex: "0 0 320px",
        maxWidth: "100%",
        background: "#f8fafc",
        border: "1px solid #e2e8f0",
        borderRadius: 14,
        padding: 12,
        display: "flex",
        flexDirection: "column",
        gap: 10,
    },

    searchBox: {
        position: "relative",
        display: "flex",
        alignItems: "center",
    },
    searchInput: {
        width: "100%",
        borderRadius: 10,
        border: "1.5px solid #e5e7eb",
        padding: "8px 12px 8px 34px",
        fontSize: 13,
        color: "#1f2937",
        background: "#fff",
        height: 40,
    },
    searchIcon: {
        position: "absolute",
        left: 12,
        color: "#94a3b8",
        fontSize: 13,
        pointerEvents: "none",
    },

    listScroll: {
        maxHeight: 520,
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
        gap: 8,
        paddingRight: 2,
    },

    // A single building entry in the left list.
    buildingItem: {
        textAlign: "left",
        width: "100%",
        background: "#fff",
        border: "1.5px solid #e5e7eb",
        borderRadius: 12,
        padding: "12px 14px",
        cursor: "pointer",
        transition: "all 0.15s ease",
        display: "flex",
        flexDirection: "column",
        gap: 8,
    },
    buildingItemActive: {
        textAlign: "left",
        width: "100%",
        background: "linear-gradient(135deg, #eef2ff 0%, #f5f3ff 100%)",
        border: "1.5px solid #a5b4fc",
        borderRadius: 12,
        padding: "12px 14px",
        cursor: "pointer",
        transition: "all 0.15s ease",
        display: "flex",
        flexDirection: "column",
        gap: 8,
        boxShadow: "0 2px 10px rgba(99,102,241,0.15)",
    },
    buildingItemName: {
        fontWeight: 700,
        fontSize: 14,
        color: "#0f172a",
        display: "flex",
        alignItems: "center",
        gap: 8,
    },
    miniChipRow: { display: "flex", flexWrap: "wrap", gap: 6 },
    miniChip: {
        fontSize: 11,
        fontWeight: 600,
        color: "#4338ca",
        background: "#eef2ff",
        border: "1px solid #e0e7ff",
        borderRadius: 6,
        padding: "2px 8px",
    },

    // Right pane: details of the selected building.
    detailPane: {
        flex: "1 1 420px",
        minWidth: 0,
        background: "#fff",
        border: "1px solid #e2e8f0",
        borderRadius: 14,
        padding: 18,
    },
    detailHeader: {
        display: "flex",
        alignItems: "center",
        gap: 12,
        flexWrap: "wrap",
        paddingBottom: 14,
        marginBottom: 14,
        borderBottom: "1px solid #f1f5f9",
    },
    detailTitle: { fontWeight: 700, fontSize: 17, color: "#0f172a", margin: 0, display: "flex", alignItems: "center", gap: 8 },

    // A floor block inside the detail pane (collapsible).
    floorBlock: {
        border: "1px solid #e2e8f0",
        borderRadius: 12,
        marginBottom: 12,
        overflow: "hidden",
    },
    floorHead: {
        display: "flex",
        alignItems: "center",
        gap: 10,
        flexWrap: "wrap",
        padding: "12px 16px",
        background: "#f8fafc",
        cursor: "pointer",
        userSelect: "none",
    },
    floorName: { fontWeight: 700, fontSize: 14, color: "#1e293b", display: "flex", alignItems: "center", gap: 8 },

    // Grid of room cards shown when a floor is expanded.
    roomGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
        gap: 12,
        padding: 16,
    },
    roomCard: {
        border: "1px solid #e5e7eb",
        borderRadius: 10,
        padding: "14px 16px",
        background: "#fff",
        display: "flex",
        flexDirection: "column",
        gap: 6,
    },
    roomName: { fontWeight: 700, fontSize: 14, color: "#0f172a", display: "flex", alignItems: "center", gap: 8 },
    roomCap: { fontSize: 12, color: "#64748b", fontWeight: 600 },

    detailPlaceholder: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        padding: "64px 20px",
        color: "#94a3b8",
        minHeight: 280,
    },

    // ── Building Creation modal: sectioned form layout ──
    modalSectionHead: {
        fontSize: 13,
        fontWeight: 700,
        color: "#4f46e5",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        display: "flex",
        alignItems: "center",
        gap: 8,
        marginBottom: 14,
        marginTop: 4,
    },
    stepBadge: {
        width: 22,
        height: 22,
        borderRadius: "50%",
        background: "#4f46e5",
        color: "#fff",
        fontSize: 12,
        fontWeight: 700,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
    },
    // A single floor laid out as its own card (numbered header + labeled fields).
    floorCard: {
        background: "#fff",
        border: "1px solid #e8ecf3",
        borderRadius: 12,
        padding: "14px 16px",
        marginBottom: 14,
        boxShadow: "0 1px 4px rgba(15,23,42,0.04)",
    },
    floorCardHead: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        fontSize: 13,
        fontWeight: 700,
        color: "#334155",
        marginBottom: 12,
        paddingBottom: 10,
        borderBottom: "1px dashed #eef2f7",
    },
    fieldLabel: {
        fontSize: 11,
        fontWeight: 700,
        color: "#64748b",
        textTransform: "uppercase",
        letterSpacing: "0.4px",
        marginBottom: 5,
        display: "block",
    },

    // Light panel that groups a form section.
    formSection: {
        background: "#f8fafc",
        border: "1px solid #eef2f7",
        borderRadius: 12,
        padding: "16px 18px",
        marginBottom: 18,
    },
    // A single room box in the "Configure Rooms" grid (Room No + Capacity side by side).
    roomBox: {
        background: "#f8fafc",
        border: "1px solid #eef2f7",
        borderRadius: 7,
        padding: "6px 8px",
        height: "100%",
    },
    roomBoxHead: {
        fontSize: 10,
        fontWeight: 700,
        color: "#4338ca",
        marginBottom: 4,
        display: "flex",
        alignItems: "center",
    },
    // Small label for the Room No / Capacity fields inside a room box.
    roomFieldLabel: {
        fontSize: 9,
        fontWeight: 700,
        color: "#6b7280",
        textTransform: "uppercase",
        letterSpacing: "0.4px",
        marginBottom: 3,
        display: "block",
    },
    roomInput: {
        width: "100%",
        borderRadius: 6,
        border: "1.5px solid #e5e7eb",
        padding: "4px 7px",
        fontSize: 11,
        height: 30,
        background: "#f9fafb",
        color: "#1f2937",
    },
};

export default S;
